import { VideoPlayerType } from '../../_common/client/types';
import { BaseComponent } from '../../base-component';
import { VideoPlayer, VideoPlayerEvent } from './video-player';

export type VideoTrackingObject = {
    product_VideoInpage: string;
    product_VideoProgress?: string;
    product_VideoQuality?: string;
    product_VideoError?: string;
    product_VideoCount?: number;
    product_VideoLength?: number;
    product_VideoPosition?: number;
};

export class VideoInPageComponent extends BaseComponent {
    public rootSelector = '.js_pdp_video-in-page';
    public assetKey: string | undefined;
    private player?: VideoPlayer;
    private playerTargetElementId = 'inPageVideoPlayerArea';
    private forDesktopView = false;

    public async init(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }
        this.forDesktopView = window.matchMedia('(min-width: 992px').matches;
    }

    private determineAssetRatio(rootElement: HTMLElement): string {
        if (this.forDesktopView) {
            return 'landscape';
        } else {
            return rootElement.dataset.assetRatio ?? 'landscape';
        }
    }

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }

        const rootElement = this.getRootElement();
        this.assetKey = rootElement.dataset.assetKey;

        if (this.assetKey === undefined) {
            this.logger?.logError({ type: 'OnHtmlLoadedError', message: 'assetKey is undefined, cannot create player.' });
            return;
        }

        try {
            this.player = this.createPlayer(this.assetKey);
            const assetRatio = this.determineAssetRatio(rootElement);
            const assetRatioLandscape = assetRatio === 'landscape';
            const videoPlayerTypeAttributeFromPdpContent = document.querySelector('.pdp-content')?.getAttribute('data-video-player-type');
            let videoPlayerType: VideoPlayerType = 'cliplister';
            if (videoPlayerTypeAttributeFromPdpContent === 'OTTO_live' || videoPlayerTypeAttributeFromPdpContent === 'OTTO_nonlive') {
                videoPlayerType = videoPlayerTypeAttributeFromPdpContent;
            }
            await this.player.initializeViewerInstance(assetRatioLandscape, videoPlayerType);
        } catch (error: unknown) {
            this.logger?.logError({
                type: 'OnHtmlLoadedError',
                message: `Es ist ein technischer Fehler aufgetreten. Video konnte nicht geladen werden. (asset ${this.assetKey}).`,
                error: error as Error,
            });
            this.trackEvent({ product_VideoInpage: (error as Error)?.message });
        }
    }

    private createPlayer(assetKey: string): VideoPlayer {
        return new VideoPlayer({
            assetKey,
            parentId: this.playerTargetElementId,
            onProgress10: playerState => this.trackPlayerEvent(playerState, 'progress_0.1'),
            onProgress50: playerState => this.trackPlayerEvent(playerState, 'progress_0.5'),
            onProgress90: playerState => this.trackPlayerEvent(playerState, 'progress_0.9'),
            onQualityChange: playerState => this.trackPlayerEvent(playerState, 'changeQuality'),
            onInitialVideoStart: playerState => this.trackPlayerEvent(playerState, 'start'),
            onFullscreenChange: playerState =>
                this.trackPlayerEvent(playerState, playerState.isFullscreen ? 'openFullscreen' : 'closeFullscreen'),
            onChangeVideo: playerState => this.trackPlayerEvent(playerState, 'changeVideo'),
            onPause: playerState => this.trackPlayerEvent(playerState, 'pause'),
            onPlayAfterPause: playerState => this.trackPlayerEvent(playerState, 'play'),
        });
    }

    private trackPlayerEvent(playerState: VideoPlayerEvent, trackingEventName: string): void {
        const trackingObject: VideoTrackingObject = {
            product_VideoInpage: trackingEventName,
            product_VideoCount: playerState.videoCount,
            product_VideoLength: playerState.videoLength,
            product_VideoPosition: playerState.videoPosition,
            product_VideoQuality: playerState.videoQuality,
        };
        this.trackEvent(trackingObject);
    }
}
